import { get, isString } from 'radash';
import { useEffect, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

import { useDataModel } from '@/hooks/useDataModel';
import { useIndexedDB } from '@/hooks/useIndexedDB';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getCustomFilter, hydrateAngularOptionListFromOptions } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { fetchItemData } from '@/services/utils';
import {
    DataModelAttribute,
    DataModelAttributeOption,
    DataModelAttributeTypeName,
    Item,
} from '@/types/DataModel';

import { filterOptionListOptionsByGroups } from '../optionListUtils';
import {
    fetchOptionListOptions,
    useCodelistPairs,
    useDynamicEnumValues,
    useOptionLists,
} from '../queries';

interface CachedOptionList {
    dataModelHash: string;
    name: string;
    options: DataModelAttributeOption[];
}

interface UseOptionsProps {
    attribute: DataModelAttribute;
    item?: FieldValues;
}
/**
 * A hook that returns the options of a data model attribute after applying the data model filter (if any). Where,
 * some attributes provide the options in a synchronous fashion, while others need to be fetched from dedicated HTTP resources or even
 * from IndexedDb.
 *
 * The hook works with the following attribute types:
 *  - Booleans
 *  - Dimensional
 *  - Enums and EnumSets
 *  - Dynamic Enums
 *  - Codelists
 *  - ServiceLists
 *  - Physicals
 */
const useOptions = ({ attribute, item }: UseOptionsProps) => {
    const [options, setOptions] = useState<DataModelAttributeOption[]>([]);
    const [rawOptions, setRawOptions] = useState<DataModelAttributeOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [optionListName, setOptionListName] = useState('');
    const [optionListServiceName, setOptionListServiceName] = useState('');

    const [codelistName, setCodelistName] = useState('');
    const { data: codelistPairs } = useCodelistPairs({
        variables: { codelistName },
        enabled: codelistName !== '',
    });

    const [dynamicEnumUrl, setDynamicEnumUrl] = useState('');
    const { data: dynamicEnumValues } = useDynamicEnumValues({
        variables: { url: dynamicEnumUrl },
        enabled: dynamicEnumUrl !== '',
    });

    const {
        add: addOptionListToCache,
        get: getOptionListFromCache,
        deleteAllByIndex: deleteOptionListsFromCache,
    } = useIndexedDB<CachedOptionList>('optionLists');

    const { data: optionListPairs } = useOptionLists({
        variables: { optionListName, serviceName: optionListServiceName },
        enabled: optionListName !== '' && optionListServiceName !== '',
    });

    const { dataModel } = useDataModel();
    const { organization } = useOrganization();
    const { user } = useUser();
    const { getValues, watch } = useFormContext();

    const getCurrentItemWithFetchedData = async (): Promise<FieldValues | undefined> => {
        if (attribute.item && Object.keys(attribute.item).length > 1) {
            return attribute.item;
        }
        const fetchedData = await fetchItemData(attribute.item.primaryKey__);
        return fetchedData;
    };

    const keyOrName = (o: DataModelAttributeOption) => o.key ?? o.name;

    // FIXME: Make this a callback after making 'item' stable
    const filterOptions = async (
        list: DataModelAttributeOption[],
        referencedOptionAttribute: DataModelAttribute | null
    ): Promise<DataModelAttributeOption[]> => {
        const filterName =
            attribute.params?.uiOptionsFilter ||
            attribute.params?.uiCodelistFilter ||
            attribute.optionsFilter ||
            referencedOptionAttribute?.optionsFilter ||
            referencedOptionAttribute?.params?.uiOptionsFilter;

        if (!filterName) return list;

        const filterFn = getCustomFilter(filterName);
        if (!filterFn) {
            logger.error(
                `useOptions: Filter ${filterName} defined for attribute ${attribute.name} was not found.`
            );
            return list;
        }

        let currentItem: FieldValues = getValues() ?? item ?? {};
        const isFromMultiReference =
            attribute.item !== undefined && attribute.additionalParams?.readonly === true;

        if (isFromMultiReference) {
            if (attribute.item?.primaryKey__ !== undefined || attribute.item?.id !== undefined) {
                const fetchedItem = await getCurrentItemWithFetchedData();
                currentItem = fetchedItem ?? currentItem;
                logger.warn(
                    'currentItem',
                    currentItem,
                    attribute.name,
                    attribute.item?.primaryKey__
                );
            } else {
                currentItem = item ?? currentItem;
            }
        } else {
            currentItem = currentItem ?? getValues() ?? {};
        }

        attribute.optionList = attribute.params?.keys;

        // Initialize currentItem[attribute.name] with one entry per option key, preserving existing values
        const optionList = attribute.optionList ?? [];
        if (Array.isArray(optionList) && optionList.length > 0) {
            const existing =
                currentItem[attribute.name] &&
                typeof currentItem[attribute.name] === 'object' &&
                !Array.isArray(currentItem[attribute.name])
                    ? (currentItem[attribute.name] as Record<string, unknown>)
                    : {};
            const initialized: Record<string, string> = {};
            for (const opt of optionList) {
                const key = keyOrName(opt as DataModelAttributeOption);
                if (key != null) {
                    const existingVal = existing[key];
                    initialized[key] =
                        existingVal !== undefined && existingVal !== ''
                            ? String(existingVal)
                            : '';
                }
            }
            currentItem[attribute.name] = initialized;
        }

        // If the attribute is being rendered in a Collection/Grid row,
        // collectionEntry should be the current row data from form state
        // We check if collectionRowPath exists and it's not from MultiReference to determine if we're in a Collection/table context
        // We use getValues to get current form values, which will be called when form values change (via watch subscription)
        const collectionRowPath = attribute.additionalParams?.collectionRowPath;
        const isInCollectionRow = Boolean(collectionRowPath) && !isFromMultiReference;
        const collectionEntry = isInCollectionRow ? getValues(collectionRowPath as string) : undefined;

        const filteredKeys = filterFn(
            list.map(keyOrName),
            currentItem as Item,
            attribute,
            // @ts-ignore: The user and organization are for sure loaded by now
            user,
            organization,
            collectionEntry
        ) as string[] | undefined;

        if (!Array.isArray(filteredKeys) || filteredKeys.length === 0) {
            return list;
        }

        // LAX-9988: Boolean-typed codelist attributes render as radio buttons
        // at the item root and inside Group entries, but as a ui-select
        // dropdown when they live inside a Collection/table row. For radio
        // buttons we keep the invalid codes visible but disabled so the user
        // still sees the full set; for table dropdowns we drop them entirely.
        if (
            attribute.typeName === DataModelAttributeTypeName.Boolean &&
            !isInCollectionRow
        ) {
            return list.map((o) =>
                filteredKeys.includes(keyOrName(o)) ? o : { ...o, disabled: true }
            );
        }

        const filteredMap = new Map(list.map((o) => [keyOrName(o), o]));
        return filteredKeys
            .map((key) => filteredMap.get(key))
            .filter((o): o is DataModelAttributeOption => o !== undefined);
    };

    // Load and cache options for the attribute (or its resolved source) on dependency changes.
    useEffect(() => {
        let alive = true;

        async function fetchOptions() {
            if (!dataModel || !organization || !user) return;

            setLoading(true);

            const referencedAttributeName =
                attribute.params?.valueAttribute || attribute.params?.keyAttribute;

            const effectiveAttribute = referencedAttributeName
                ? dataModel.attribute(referencedAttributeName) ?? attribute
                : attribute;

            let referencedOptionAttribute = null;

            if (referencedAttributeName && effectiveAttribute === attribute) {
                logger.error(
                    `useOptions: Could not find attribute ${referencedAttributeName} defined as reference attribute for ${attribute.name}`
                );
            } else {
                referencedOptionAttribute = effectiveAttribute;
            }

            // Dynamic Enums (defer to query hook)
            if (effectiveAttribute.params?.dynamicValuesUrl) {
                setDynamicEnumUrl(effectiveAttribute.params.dynamicValuesUrl);
                setLoading(false); // the query hook will set options later
                return;
            }

            // Values inline or by option list name
            if (effectiveAttribute.params?.values) {
                const values = effectiveAttribute.params.values;

                // Option list (name)
                if (isString(values)) {
                    const requestedOptionListName = values;
                    const cachedOptionList = await getOptionListFromCache([
                        requestedOptionListName,
                        organization.dataModelHash,
                    ]);

                    if (!cachedOptionList) {
                        // Bust cache entries with same name but old hashes
                        await deleteOptionListsFromCache('name', requestedOptionListName);

                        // Try from data model first (skip [] for service lists until optionsLoaded, or Angular refetches)
                        const optionList = dataModel.optionList(requestedOptionListName);
                        const dmOptions = optionList?.options;
                        if (Array.isArray(dmOptions) &&
                            dmOptions.length > 0 &&
                            (!optionList?.serviceName || optionList.optionsLoaded === true)) {
                            const filtered = await filterOptions(dmOptions, referencedOptionAttribute);
                            if (!alive) return;
                            setRawOptions(dmOptions);
                            setOptions(filtered);
                            setLoading(false);
                        } else {
                            // Needs server; kick off query hook
                            setOptionListName(requestedOptionListName);
                            setOptionListServiceName(optionList.serviceName);
                            setLoading(false);
                        }
                    } else {
                        logger.debug(
                            `useOptions: Loaded enum ${effectiveAttribute.name} option(s) with option list ${requestedOptionListName} from cache`
                        );
                        // Hydrate runs translateAllOptions on its mapped copy; use that copy
                        // for React state so consumers see translated `option.translatedOption`.
                        const translated = hydrateAngularOptionListFromOptions(
                            dataModel,
                            requestedOptionListName,
                            cachedOptionList.options
                        );
                        const filtered = await filterOptions(translated, referencedOptionAttribute);
                        if (!alive) return;
                        setRawOptions(translated);
                        setOptions(filtered);
                        setLoading(false);
                    }

                    return;
                }

                const inlineOptions = values as DataModelAttributeOption[];
                const filtered = await filterOptions(inlineOptions, referencedOptionAttribute);
                if (!alive) return;
                setRawOptions(inlineOptions);
                setOptions(filtered);
                setLoading(false);
                return;
            }

            // Dimensional keys
            if (effectiveAttribute.params?.keys) {
                const keys = effectiveAttribute.params.keys;
                const groupNames = get(effectiveAttribute.params, 'keysOptionGroups') as string[];

                if (isString(keys)) {
                    const optionListForKeys = dataModel.optionList(keys);

                    // For service-backed lists, follow the same cache waterfall as the
                    // values branch above:
                    //   1. IndexedDB cache hit → reuse,
                    //   2. AngularJS in-memory options (post-load) → reuse,
                    //   3. Otherwise fetch fresh and persist to IndexedDB.
                    // For non-service-backed lists, defer to the AngularJS path which already
                    // owns its own caching + group-filter inside dataModel.optionListOptionsAsync.
                    let unfilteredOptions: DataModelAttributeOption[];
                    if (optionListForKeys?.serviceName) {
                        const cachedOptionList = await getOptionListFromCache([
                            keys,
                            organization.dataModelHash,
                        ]);
                        if (cachedOptionList) {
                            unfilteredOptions = hydrateAngularOptionListFromOptions(
                                dataModel,
                                keys,
                                cachedOptionList.options
                            );
                        } else if (
                            Array.isArray(optionListForKeys.options) &&
                            optionListForKeys.options.length > 0 &&
                            optionListForKeys.optionsLoaded === true
                        ) {
                            unfilteredOptions = optionListForKeys.options;
                        } else {
                            await deleteOptionListsFromCache('name', keys);
                            const fetched = await fetchOptionListOptions(
                                keys,
                                optionListForKeys.serviceName
                            );
                            unfilteredOptions = hydrateAngularOptionListFromOptions(
                                dataModel,
                                keys,
                                fetched
                            );
                            // Persist to IndexedDB so subsequent reloads (and other React
                            // consumers using the same dataModelHash) can short-circuit.
                            await addOptionListToCache(
                                [keys, organization.dataModelHash],
                                {
                                    dataModelHash: organization.dataModelHash,
                                    name: keys,
                                    options: unfilteredOptions,
                                }
                            );
                        }
                    } else {
                        unfilteredOptions = await dataModel.optionListOptionsAsync(keys, groupNames);
                    }

                    // Apply group filter for the React fetch path (the AngularJS path applied
                    // it inside optionListOptionsAsync already, so don't double-filter).
                    const dimensionalOptions = optionListForKeys?.serviceName
                        ? filterOptionListOptionsByGroups(
                            unfilteredOptions,
                            optionListForKeys.groups,
                            groupNames
                        )
                        : unfilteredOptions;

                    const filtered = await filterOptions(dimensionalOptions, referencedOptionAttribute);
                    if (!alive) return;
                    setRawOptions(dimensionalOptions);
                    setOptions(filtered);
                    setLoading(false);
                    return;
                } else {
                    // (Rare) keys already provided as options array
                    const provided = keys as unknown as DataModelAttributeOption[];
                    const filtered = await filterOptions(provided, referencedOptionAttribute);
                    if (!alive) return;
                    setRawOptions(provided);
                    setOptions(filtered);
                    setLoading(false);
                    return;
                }
            }

            // Codelists
            if (effectiveAttribute.params?.codelist) {
                setCodelistName(effectiveAttribute.params.codelist);
                setLoading(false);
                return;
            }

            // Fallback: nothing to load
            if (!alive) return;
            setRawOptions([]);
            setOptions([]);
            setLoading(false);
        }

        fetchOptions();

        return () => {
            alive = false;
        };
    }, [
        attribute,
        dataModel,
        organization,
        user,
        item?.targetMarket,
    ]);

    // Re-apply option filters when form state changes (watch subscription).
    useEffect(() => {
        if (!dataModel) return;

        const referencedOptionAttribute = dataModel.getReferencedOptionAttribute(attribute) ?? null;

        const filterName =
            attribute.params?.uiOptionsFilter ||
            attribute.params?.uiCodelistFilter ||
            attribute.optionsFilter ||
            referencedOptionAttribute?.optionsFilter;

        if (!filterName) return;
        if (!rawOptions || rawOptions.length === 0) return;
        if (!organization || !user) return;

        let alive = true;
        let latestRun = 0;
        let timer: number | undefined;

        const subscription = watch(() => {
            if (timer) window.clearTimeout(timer);
            timer = window.setTimeout(async () => {
                const runId = ++latestRun;
                const filtered = await filterOptions(rawOptions, referencedOptionAttribute);
                if (!alive) return;
                if (runId !== latestRun) return;
                setOptions(filtered);
            }, 75);
        });

        (async () => {
            const runId = ++latestRun;
            const filtered = await filterOptions(rawOptions, referencedOptionAttribute);
            if (!alive) return;
            if (runId !== latestRun) return;
            setOptions(filtered);
        })();

        return () => {
            alive = false;
            if (timer) window.clearTimeout(timer);
            subscription.unsubscribe();
        };
    }, [attribute, rawOptions, organization, user, watch, dataModel, getValues]);

    // Apply codelist query results to options.
    useEffect(() => {
        if (!dataModel) return;

        const referencedOptionAttribute = dataModel.getReferencedOptionAttribute(attribute) ?? null;

        let alive = true;

        (async () => {
            if (codelistPairs) {
                setLoading(true);
                const filtered = await filterOptions(codelistPairs, referencedOptionAttribute);
                if (!alive) return;
                setRawOptions(codelistPairs);
                setOptions(filtered);
                setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [codelistPairs, dataModel, attribute, organization, user]);

    // Apply dynamic-enum query results to options.
    useEffect(() => {
        if (!dataModel) return;

        const referencedOptionAttribute = dataModel.getReferencedOptionAttribute(attribute) ?? null;

        let alive = true;

        (async () => {
            if (dynamicEnumValues) {
                setLoading(true);
                const filtered = await filterOptions(dynamicEnumValues, referencedOptionAttribute);
                if (!alive) return;
                setRawOptions(dynamicEnumValues);
                setOptions(filtered);
                setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [dynamicEnumValues, dataModel, attribute, organization, user]);

    // Cache and apply option-list query results to options.
    useEffect(() => {
        if (!dataModel) return;

        const referencedOptionAttribute = dataModel.getReferencedOptionAttribute(attribute) ?? null;

        let alive = true;

        (async () => {
            if (optionListPairs && organization) {
                await addOptionListToCache([optionListName, organization.dataModelHash], {
                    dataModelHash: organization.dataModelHash,
                    name: optionListName,
                    options: optionListPairs,
                });

                setLoading(true);
                // Hydrate runs translateAllOptions on its mapped copy; use that copy
                // for React state so consumers see translated `option.translatedOption`.
                const translated = hydrateAngularOptionListFromOptions(
                    dataModel,
                    optionListName,
                    optionListPairs
                );
                const filtered = await filterOptions(translated, referencedOptionAttribute);
                if (!alive) return;
                setRawOptions(translated);
                setOptions(filtered);
                setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [
        optionListPairs,
        organization,
        optionListName,
        addOptionListToCache,
        dataModel,
        attribute,
        user,
    ]);

    return { options, loading };
};

export { useOptions };
